"use client"

import { useEffect, useMemo } from "react"
import debounce from "lodash.debounce"
import { captureAnalyticsEvent } from "@/utils/analytics.utils"
import { useWindowManager, DOCK_APPS } from "./useWindowManager"
import { useIsSetupAppVisible } from "./useIsSetupAppVisible"
import { getStagedWindow, useDesktopStore } from "@/stores/desktopStore"
import { openChatWindow, toggleChat } from "@/stores/chatAppStore"
import { RECORD_WINDOW_TYPES } from "./types"
import type {
    DockAppConfig,
    WindowState,
    WindowType,
    WindowOpenSource,
} from "./types"
import { WINDOW_REGISTRY_METADATA as WINDOW_REGISTRY } from "./windowRegistry.metadata"

const RECORD_DOCK_TYPES: ReadonlySet<WindowType> = new Set([
    ...RECORD_WINDOW_TYPES,
    "onboarding",
    "meetWilson",
])

const CHAT_APP: DockAppConfig[] = DOCK_APPS.flatMap(app =>
    app.type === "wilson" ? [{ ...app, id: "chat", label: "Chat" }] : []
)

export const SWITCHER_APPS: DockAppConfig[] = [
    ...CHAT_APP,
    ...DOCK_APPS.filter(app => app.type !== "wilson"),
]
const SWITCHER_APPS_WITHOUT_SETUP: DockAppConfig[] = SWITCHER_APPS.filter(
    app => app.type !== "setup"
)

export const switcherAppsOf = ({
    isSetupAppVisible,
}: {
    isSetupAppVisible: boolean
}): DockAppConfig[] =>
    isSetupAppVisible ? SWITCHER_APPS : SWITCHER_APPS_WITHOUT_SETUP

function revealExistingWindow({
    windowId,
    windowType,
    source,
}: {
    windowId: string
    windowType: WindowType
    source: WindowOpenSource
}) {
    captureAnalyticsEvent("window_opened", {
        windowType,
        source,
        isNew: false,
    })
    useDesktopStore.getState().revealWindow(windowId)
}

export function getHomeApp({
    type,
    isSetupAppVisible,
}: {
    type: WindowType
    isSetupAppVisible: boolean
}): DockAppConfig | undefined {
    const homeType = WINDOW_REGISTRY[type].homeApp
    return switcherAppsOf({ isSetupAppVisible }).find(
        app => app.type === homeType
    )
}

export function useDockItems() {
    const { windows, activeWindowId, openWindow, closeWindow } =
        useWindowManager()
    const isSetupAppVisible = useIsSetupAppVisible()

    const debouncedOpenExplorer = useMemo(
        () =>
            debounce(
                (source: WindowOpenSource) =>
                    openWindow("explorer", { source }),
                300,
                {
                    leading: true, // boolean-naming-exempt: external contract — lodash.debounce option
                    trailing: false, // boolean-naming-exempt: external contract — lodash.debounce option
                }
            ),
        [openWindow]
    )

    useEffect(
        () => () => debouncedOpenExplorer.cancel(),
        [debouncedOpenExplorer]
    )

    const windowsByType = useMemo(() => {
        const map: Partial<Record<WindowType, WindowState>> = {}
        for (const w of windows) {
            if (!map[w.type]) map[w.type] = w
        }
        return map
    }, [windows])

    const recordWindows = useMemo(
        () => windows.filter(w => RECORD_DOCK_TYPES.has(w.type)),
        [windows]
    )

    const selectApp = ({
        app,
        source,
        isModifierClick = false,
        shouldSendBackStaged = true,
    }: {
        app: DockAppConfig
        source: WindowOpenSource
        isModifierClick?: boolean
        shouldSendBackStaged?: boolean
    }) => {
        if (app.type === "wilson") {
            captureAnalyticsEvent("window_opened", {
                windowType: app.type,
                source,
                isNew: false,
            })
            if (shouldSendBackStaged) toggleChat()
            else openChatWindow()
            return
        }
        const currentWindows = useDesktopStore.getState().windows
        const typeWindows = currentWindows.filter(w => w.type === app.type)

        if (typeWindows.length === 0) {
            if (app.type === "explorer") {
                debouncedOpenExplorer(source)
            } else {
                openWindow(app.type, { source })
            }
            return
        }
        if (isModifierClick) {
            revealExistingWindow({
                windowId: typeWindows[0].id,
                windowType: app.type,
                source,
            })
            return
        }
        const target = typeWindows.reduce((a, b) =>
            b.zIndex > a.zIndex ? b : a
        )
        if (getStagedWindow(currentWindows)?.id === target.id) {
            if (shouldSendBackStaged) {
                useDesktopStore.getState().minimizeWindow(target.id)
            }
            return
        }
        revealExistingWindow({
            windowId: target.id,
            windowType: app.type,
            source,
        })
    }

    const selectRecord = ({
        windowState,
        source,
        shouldSendBackStaged = true,
    }: {
        windowState: WindowState
        source: WindowOpenSource
        shouldSendBackStaged?: boolean
    }) => {
        const currentWindows = useDesktopStore.getState().windows
        if (getStagedWindow(currentWindows)?.id === windowState.id) {
            if (shouldSendBackStaged) {
                useDesktopStore.getState().minimizeWindow(windowState.id)
            }
            return
        }
        revealExistingWindow({
            windowId: windowState.id,
            windowType: windowState.type,
            source,
        })
    }

    const isAppOpen = (app: DockAppConfig) => !!windowsByType[app.type]

    return {
        windows,
        activeWindowId,
        isAppOpen,
        apps: switcherAppsOf({ isSetupAppVisible }),
        windowsByType,
        recordWindows,
        selectApp,
        selectRecord,
        closeWindow,
    }
}
